import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Building2, Check } from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";
import { useCompany } from "../../context/CompanyContext";
import RutInput from "../../components/RutInput";
import { isValidRut } from "../../lib/rut";

const inputCls =
  "w-full bg-ink-900 border border-ink-500 text-ash-100 focus:border-brand outline-none px-3 py-2 text-sm placeholder:text-ash-500";

const COLORS = ["#7BA6B0", "#5B8FAF", "#D97757", "#D9A557", "#1B7E3F", "#9B1C1C", "#9B5BBA", "#4A555C"];

export default function AdminCompanies() {
  const { companies, refresh, active, setActiveId } = useCompany();
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const newCompany = () => setEditing({
    name: "", rut: "", industry: "", contact_email: "", contact_phone: "", notes: "",
    color: COLORS[0], is_active: true,
  });

  const remove = async (c) => {
    const ok = await confirm({
      title: `¿Eliminar empresa "${c.name}"?`,
      message: "Si la empresa tiene prospectos asociados, primero deberás moverlos o eliminarlos.",
      confirmLabel: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/companies/${c.id}`);
      toast.success("Empresa eliminada");
      if (active?.id === c.id) setActiveId(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div data-testid="admin-companies-page" className="p-6 md:p-8">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-1">Administración</div>
          <h1 className="font-display font-bold text-2xl md:text-3xl text-ash-100">Empresas</h1>
          <p className="text-ash-300 text-sm mt-2 max-w-2xl">
            Gestiona las empresas que usan la plataforma. Cada una mantiene su propio CRM y Agenda. Usa el selector en la barra superior para cambiar de empresa activa.
          </p>
        </div>
        <button
          data-testid="company-create"
          onClick={newCompany}
          className="inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 py-2.5 uppercase tracking-widest text-xs"
        >
          <Plus size={14} /> Nueva empresa
        </button>
      </div>

      <div className="bg-ink-800 border border-ink-500 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#4A555C] text-white">
              <Th>Empresa</Th>
              <Th>Industria</Th>
              <Th>RUT</Th>
              <Th>Contacto</Th>
              <Th>Estado</Th>
              <Th className="text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => (
              <tr
                key={c.id}
                data-testid={`company-row-${c.id}`}
                className="relative border-t border-ink-500 hover:bg-ink-700/40"
                style={{ boxShadow: `inset 4px 0 0 0 ${c.color || "#7BA6B0"}` }}
              >
                <Td>
                  <div className="flex items-center gap-3 pl-2">
                    <span
                      className="w-6 h-6 shrink-0 flex items-center justify-center text-[11px] font-bold text-white shadow-sm"
                      style={{
                        background: c.color || "#7BA6B0",
                        boxShadow: `0 0 0 1px ${c.color || "#7BA6B0"}, 0 0 12px ${c.color || "#7BA6B0"}55`,
                      }}
                      title={c.name}
                    >
                      {(c.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <div>
                      <div className="font-medium text-ash-100">{c.name}</div>
                      {active?.id === c.id && (
                        <div className="text-[10px] uppercase tracking-widest text-brand font-mono mt-0.5">Activa</div>
                      )}
                    </div>
                  </div>
                </Td>
                <Td>{c.industry || "—"}</Td>
                <Td className="font-mono text-xs">{c.rut || "—"}</Td>
                <Td>
                  <div className="text-[11px] text-ash-300">
                    {c.contact_email && <div>{c.contact_email}</div>}
                    {c.contact_phone && <div>{c.contact_phone}</div>}
                    {!c.contact_email && !c.contact_phone && "—"}
                  </div>
                </Td>
                <Td>
                  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${
                    c.is_active ? "bg-[#1B7E3F]/20 text-[#7CD49B] border border-[#1B7E3F]/40" : "bg-ash-500/20 text-ash-500 border border-ash-500/40"
                  }`}>
                    {c.is_active ? "Activa" : "Inactiva"}
                  </span>
                </Td>
                <Td className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => setActiveId(c.id)}
                      title="Establecer como activa"
                      disabled={active?.id === c.id}
                      className="p-1.5 border border-ink-500 hover:border-brand hover:text-brand text-ash-300 disabled:opacity-30"
                    >
                      <Check size={13} />
                    </button>
                    <button
                      data-testid={`company-edit-${c.id}`}
                      onClick={() => setEditing({ ...c })}
                      className="p-1.5 border border-ink-500 hover:border-brand hover:text-brand text-ash-300"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      data-testid={`company-delete-${c.id}`}
                      onClick={() => remove(c)}
                      className="p-1.5 border border-ink-500 hover:border-[#9B1C1C] hover:text-[#FF7A7A] text-ash-300"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
            {companies.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-ash-500 py-16">
                  <Building2 size={28} className="mx-auto mb-3 opacity-50" />
                  Aún no has creado empresas. Crea la primera para comenzar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal company={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      )}
    </div>
  );
}

function Modal({ company, onClose, onSaved }) {
  const [form, setForm] = useState(company);
  const [saving, setSaving] = useState(false);
  const isNew = !company.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (form.rut && !isValidRut(form.rut)) { toast.error("El RUT no es válido"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        rut: form.rut || null,
        industry: form.industry || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        notes: form.notes || null,
        color: form.color || null,
        is_active: !!form.is_active,
      };
      if (isNew) await api.post("/admin/companies", payload);
      else await api.patch(`/admin/companies/${company.id}`, payload);
      toast.success(isNew ? "Empresa creada" : "Empresa actualizada");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-800 border border-ink-500 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-ink-500 flex items-center justify-between">
          <h2 className="font-display font-bold text-xl text-ash-100">
            {isNew ? "Nueva empresa" : "Editar empresa"}
          </h2>
          <button onClick={onClose} className="text-ash-500 hover:text-ash-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre *"><input className={inputCls} value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Industria"><input className={inputCls} value={form.industry || ""} onChange={(e) => set("industry", e.target.value)} /></Field>
            <Field label="RUT">
              <RutInput
                value={form.rut || ""}
                onChange={(v) => set("rut", v)}
                className={inputCls}
                testId="company-rut"
              />
            </Field>
            <Field label="Email de contacto"><input className={inputCls} value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} /></Field>
            <Field label="Teléfono"><input className={inputCls} value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
            <Field label="Estado">
              <label className="inline-flex items-center gap-2 text-sm text-ash-100 mt-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} className="accent-brand" />
                Empresa activa
              </label>
            </Field>
          </div>
          <Field label="Color identificador">
            <div className="flex flex-wrap gap-2">
              {COLORS.map((col) => (
                <button
                  key={col}
                  type="button"
                  onClick={() => set("color", col)}
                  className={`w-8 h-8 border-2 ${form.color === col ? "border-ash-100" : "border-transparent"}`}
                  style={{ background: col }}
                  title={col}
                />
              ))}
            </div>
          </Field>
          <Field label="Notas">
            <textarea rows={3} className={inputCls} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-ink-500 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2.5 text-xs uppercase tracking-widest border border-ink-500 text-ash-100 hover:border-brand">Cancelar</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="company-save"
            className="px-5 py-2.5 text-xs uppercase tracking-widest font-semibold bg-brand text-ink-800 hover:bg-brand-hover disabled:opacity-50"
          >
            {saving ? "Guardando…" : (isNew ? "Crear" : "Guardar")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-ash-300 uppercase tracking-widest mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left text-[10px] uppercase tracking-widest font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}
